import _mapValues from 'lodash/mapValues';
import _merge from 'lodash/merge';
import queryString from 'query-string';

import DeviceProps from '@magnus/react-native-device-props';
import UserProps from '@magnus/react-native-user-props';
import EvTruck from '@magnus/react-native-evtruck';

import Web2App from '@wowmaking/web2app';

import { disabled } from '@web-solutions/module-analytics';

export type AppLinks = {
  ADJUST_LINK?: string;
  ADJUST_TOKEN?: string;
  APPSFLYER_LINK?: string;
  ADJUST_LINK_ANDROID?: string;
  ADJUST_TOKEN_ANDROID?: string;
  WEB_APP_LINK?: string;
}

type URLBaseParamsType = {
  urlExtraPath?: string;
  query?: Record<string, string>
}

let IDFM: string | undefined;
let appLinks: AppLinks | undefined;
let rcTarget: string | undefined;

let URLBaseParams: URLBaseParamsType = {
  urlExtraPath: '',
  query: {}
};


export enum LinkTarget {
  DEFAULT = 'default', // empty - by default - go to AF/Adjust link and then to store
  WEB = 'web', // target is web app
}

/**
 * Facebook ad labels the funnel forwards to the backend so revenue can be tied
 * to a campaign/adset/creative in the cohort (ROMI) report. Field names and the
 * length limits are the backend contract — see the "Проброс рекламных меток
 * Facebook в бэкенд" brief.
 */
export type AdAttribution = {
  campaignId?: string;
  adsetId?: string;
  adId?: string;
  fbclid?: string;
  source?: string;
};

/**
 * Exported so the app's fresh-session storage wipe can carry it across: the ad
 * click happens once, and the labels must outlive a `localStorage.clear()` that
 * is only meant to reset the questionnaire.
 */
export const AD_ATTRIBUTION_STORAGE_KEY = 'ad_attribution';

/**
 * [URL params to look under, payload field, max length the backend accepts].
 *
 * Two spellings per id on purpose: the brief specified `utm_campaign` /
 * `utm_adset` / `utm_ad`, but the live Ads Manager links are set up with
 * `campaign_id` / `adset_id` / `ad_id` — so reading only the brief's names left
 * every real buyer with no labels at all. First name that has a value wins.
 *
 * `campaign_name` / `adset_name` / `ad_name` / `ad_placement` / `ad_subnetwork`
 * are deliberately absent: the backend joins spend by id and does not take them.
 */
const AD_ATTRIBUTION_PARAMS: Array<[string[], keyof AdAttribution, number]> = [
  [['utm_campaign', 'campaign_id'], 'campaignId', 64],
  [['utm_adset', 'adset_id'], 'adsetId', 64],
  [['utm_ad', 'ad_id'], 'adId', 64],
  [['fbclid'], 'fbclid', 512],
  [['utm_source'], 'source', 64],
];

function readStoredAdAttribution(): AdAttribution | undefined {
  try {
    const raw = window.localStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === 'object' ? parsed as AdAttribution : undefined;
  } catch (_) {
    // Broken JSON / storage disabled — attribution is never worth an exception.
    return undefined;
  }
}

/**
 * Snapshot the FB ad labels from the current URL into localStorage.
 *
 * Only writes when the URL actually carries at least one of them, so a reload,
 * an in-funnel navigation or a later organic visit never wipes what the ad click
 * brought in. Nothing is invented: a param that isn't there produces no field.
 */
function captureAdAttribution(): void {
  try {
    const params = queryString.parse(window.location.search);

    const snapshot: AdAttribution = {};

    AD_ATTRIBUTION_PARAMS.forEach(([names, field, maxLength]) => {
      for (const name of names) {
        const raw = params[name];
        const value = Array.isArray(raw) ? raw[0] : raw;

        if (typeof value === 'string' && value) {
          snapshot[field] = value.slice(0, maxLength);
          break;
        }
      }
    });

    if (!Object.keys(snapshot).length) {
      return;
    }

    window.localStorage.setItem(AD_ATTRIBUTION_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_) {
    // Private mode / storage disabled — the funnel must keep working.
  }
}


const AttributionModule = {
  init(links: AppLinks) {
    // Must run on every landing, before anything else here: the labels only
    // exist in the URL of the very first screen, and the purchase happens many
    // screens (and reloads) later.
    captureAdAttribution();

    appLinks = links;

    DeviceProps.getIDFM().then((idfm) => {
      IDFM = idfm;
    });

    const params = queryString.parse(window.location.search);

    const attribution = _mapValues({
      utm_source: params.utm_source || '',
      campaign_name: params.campaign_name || '',
      campaign_id: params.campaign_id || '',
      adset_name: params.adset_name || '',
      adset_id: params.adset_id || '',
      ad_name: params.ad_name || '',
      ad_id: params.ad_id || '',
      http_referer: window.document.referrer || '',
      ad_subnetwork: params.ad_subnetwork || '',
      ad_keyword: params.ad_keyword || '',
      ad_placement: params.ad_placement || '',
    }, String);

    if (!disabled) {
      EvTruck.trackWebAttribution(attribution);

      Web2App.init({
        onChange: (system, p) => {
          // оборачиваем в массив, в будущем сможем поддерживать несколько пикселей на сайте и тп
          EvTruck.trackWebMeasurements({
            external_id: Web2App.getAttribution().web_external_id,
            [system]: [p],
          });
        },
      });

      if (appLinks.ADJUST_LINK && appLinks.ADJUST_TOKEN) {
        return Web2App.trackAdjustImpression(appLinks.ADJUST_TOKEN);
      }

      if (appLinks.ADJUST_LINK_ANDROID && appLinks.ADJUST_TOKEN_ANDROID) {
        return Web2App.trackAdjustImpression(appLinks.ADJUST_TOKEN_ANDROID);
      }
    }
  },

  captureAdAttribution,

  /**
   * The stored FB ad labels, ready to be put on the top level of an API request
   * body as `attribution`. `undefined` for an organic visit — the object is then
   * omitted from the request entirely rather than sent empty.
   *
   * The backend keeps the FIRST snapshot it ever receives for a user and
   * silently ignores every later one, so sending this on every request is safe
   * and deliberate — no deduplication is needed here.
   */
  getAdAttribution(): AdAttribution | undefined {
    const stored = readStoredAdAttribution();

    if (!stored) {
      return undefined;
    }

    const attribution: AdAttribution = {};

    AD_ATTRIBUTION_PARAMS.forEach(([, field]) => {
      const value = stored[field];

      if (typeof value === 'string' && value) {
        attribution[field] = value;
      }
    });

    // Spend is joined by campaign/ad id. A snapshot with only `fbclid` and/or
    // `source` matches nothing, the backend discards it — and since it stamps
    // first-touch and never overwrites, sending one would burn the user's only
    // slot on a snapshot that can never be attributed.
    if (!attribution.campaignId && !attribution.adId) {
      return undefined;
    }

    return attribution;
  },

  setURLBaseParams(params: URLBaseParamsType) {
    URLBaseParams = _merge(URLBaseParams, params)
  },

  setRemoteConfigTarget(target: string) {
    rcTarget = target;
  },

  getMailLinkTarget() {
    return appLinks?.WEB_APP_LINK && rcTarget === LinkTarget.WEB ? LinkTarget.WEB : undefined;
  },

  async getLinkToApp(linkTargetExtra?: LinkTarget) {
    const source = 'sub';
    const params = queryString.parse(window.location.search);

    if (!appLinks) {
      return Promise.reject();
    }

    const up = await UserProps.fetch() as { utm_source: string, campaign_id: string, target: string, };
    const linkTarget = linkTargetExtra || up.target;
    const rcTargetExtra = linkTargetExtra || rcTarget;

    const w = appLinks.WEB_APP_LINK
      ? Promise.resolve(
        queryString.stringifyUrl({
          url: `${appLinks.WEB_APP_LINK}${URLBaseParams.urlExtraPath}`,
          query: {
            idfm: IDFM,
            source,
            mode: params.mode,
            utm_source: up.utm_source,
            campaign_id: up.campaign_id,
            ...URLBaseParams.query
          },
        })
      )
      : null;

    if ((linkTarget === LinkTarget.WEB || rcTargetExtra === LinkTarget.WEB) && w) {
      return w;
    } else if (appLinks.APPSFLYER_LINK) {
      return Web2App.createAppsFlyerLink(appLinks.APPSFLYER_LINK, { uuid: IDFM, is_retargeting: true, source });
    } else if (appLinks.ADJUST_LINK && appLinks.ADJUST_TOKEN) {
      const adj_redirect_android =
        appLinks.ADJUST_LINK_ANDROID && appLinks.ADJUST_TOKEN_ANDROID
          ? Web2App.createAdjustLink(appLinks.ADJUST_LINK_ANDROID, appLinks.ADJUST_TOKEN_ANDROID, {
            uuid: IDFM,
            source,
            mode: params.mode,
          })
          : undefined;

      return Web2App.createAdjustLink(appLinks.ADJUST_LINK, appLinks.ADJUST_TOKEN, {
        uuid: IDFM,
        source,
        mode: params.mode,
        adj_redirect_android,
      });

    } else {
      return Promise.reject(Error('Cannot create app link'));
    }
  },
};

export default AttributionModule;
